import * as React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { Radio, RadioGroup } from "@/shared/foundation/primitives";

import { DatasetPicker } from "./DatasetPicker";
import { FormBlock } from "./FormBlock";

type DestinationMode = "source" | "dataset";

export interface OutputDestinationFieldProps {
  /** Where the invocation block lives in the enclosing form. */
  name: string;
  /**
   * Whether the invocation runs on a dataset its output can go back to. Without
   * one (a schedule), the only choice is an optional dataset.
   */
  hasSourceDataset: boolean;
}

/** Where whatever the invocation produces is uploaded. */
export function OutputDestinationField({
  name,
  hasSourceDataset,
}: OutputDestinationFieldProps) {
  const { control, setValue } = useFormContext();
  const titleId = React.useId();
  const mode = useWatch({
    control,
    name: `${name}.outputDestination.mode`,
  }) as DestinationMode;

  if (!hasSourceDataset) {
    return (
      <FormBlock
        titleId={titleId}
        title="Upload action output to"
        hint="Where the files the invocation writes end up. Leave empty if the action writes no files."
        data-testid="output-destination"
      >
        <Controller
          control={control}
          name={`${name}.outputDestination.datasetId`}
          render={({ field }) => (
            <DatasetPicker
              value={typeof field.value === "string" ? field.value : null}
              onValueChange={(datasetId) => {
                field.onChange(datasetId);
                setValue(
                  `${name}.outputDestination.mode`,
                  datasetId === null ? "source" : "dataset",
                );
              }}
              aria-labelledby={titleId}
            />
          )}
        />
      </FormBlock>
    );
  }

  return (
    <FormBlock
      titleId={titleId}
      title="Upload action output to"
      hint="Where the files the invocation writes end up."
      data-testid="output-destination"
    >
      <Controller
        control={control}
        name={`${name}.outputDestination.mode`}
        render={({ field }) => (
          <RadioGroup<DestinationMode>
            appearance="card"
            aria-labelledby={titleId}
            value={mode}
            onValueChange={field.onChange}
          >
            <Radio
              value="source"
              label="The dataset that changed"
              description="Output goes back to the dataset the trigger fired in."
            />
            <Radio
              value="dataset"
              label="Another dataset"
              description="Output goes to one dataset you choose, for every run."
            />
          </RadioGroup>
        )}
      />

      {mode === "dataset" && (
        <Controller
          control={control}
          name={`${name}.outputDestination.datasetId`}
          render={({ field, fieldState }) => (
            <DatasetPicker
              value={typeof field.value === "string" ? field.value : null}
              onValueChange={field.onChange}
              label="Destination dataset"
              error={fieldState.error?.message}
            />
          )}
        />
      )}
    </FormBlock>
  );
}
